import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { RadialProgress } from "@/components/ui/radial-progress";

const forest = "#274568";
const gold = "#D4A140";

function StatTile({ label, value, suffix = "" }) {
    return (
        <Card className="border-border">
            <CardContent className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {label}
                </div>
                <div className="font-display text-3xl text-primary mt-2">
                    {value}
                    {suffix && <span className="text-base text-muted-foreground ml-1">{suffix}</span>}
                </div>
            </CardContent>
        </Card>
    );
}

export default function InstructorAnalytics({ programId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/instructor/programs/${programId}/analytics`).then((r) => setData(r.data));
    }, [programId]);

    if (!data) {
        return <div className="py-16 text-center text-muted-foreground">Loading analytics…</div>;
    }

    const lineData = data.enrollments_over_time.map((d) => ({
        date: d.date.slice(5), // MM-DD
        enrollments: d.count,
    }));

    return (
        <div className="space-y-6" data-testid="instructor-analytics">
            <Card className="border-border">
                <CardContent className="p-6 flex items-center gap-6 flex-wrap">
                    {data.progress_summary ? (
                        <RadialProgress
                            size={148}
                            lessonsCompleted={data.progress_summary.lessons_completed || 0}
                            totalLessons={data.progress_summary.total_lessons || 0}
                            chaptersCompleted={data.progress_summary.chapters_completed || 0}
                            totalChapters={data.progress_summary.total_chapters || 0}
                            flagged={data.progress_summary.flagged_attempts || 0}
                            testId="instructor-cohort-ring"
                        />
                    ) : null}
                    <div className="min-w-0 flex-1">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Cohort progress
                        </div>
                        <h3 className="font-display text-2xl text-primary mt-1">
                            Where the room stands
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                            Outer ring shows lessons completed across every enrolled student.
                            Inner ring shows chapter mastery (quizzes passed). The red badge
                            counts attempts our anti-cheat watcher flagged for review.
                        </p>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatTile
                    label="Students"
                    value={data.totals.total_students}
                />
                <StatTile
                    label="Completions"
                    value={data.totals.completions}
                />
                <StatTile
                    label="Completion rate"
                    value={data.totals.completion_rate}
                    suffix="%"
                />
                <StatTile
                    label="Avg quiz score"
                    value={data.totals.average_quiz_score}
                    suffix="/ 10"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-border">
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Enrollments · last 30 days
                        </div>
                        <h3 className="font-display text-xl text-primary mt-1 mb-4">
                            Intake trend
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={lineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E1DAC5" />
                                    <XAxis
                                        dataKey="date"
                                        stroke="#5C6B64"
                                        fontSize={11}
                                        tickLine={false}
                                        interval={4}
                                    />
                                    <YAxis
                                        stroke="#5C6B64"
                                        fontSize={11}
                                        tickLine={false}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: "#FFFFFF",
                                            border: "1px solid #E1DAC5",
                                            borderRadius: 4,
                                            fontSize: 12,
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="enrollments"
                                        stroke={forest}
                                        strokeWidth={2}
                                        dot={{ r: 3, fill: gold }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Chapter funnel
                        </div>
                        <h3 className="font-display text-xl text-primary mt-1 mb-4">
                            Students who passed each chapter
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.chapter_funnel}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E1DAC5" />
                                    <XAxis
                                        dataKey="chapter"
                                        stroke="#5C6B64"
                                        fontSize={12}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#5C6B64"
                                        fontSize={11}
                                        tickLine={false}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: "#FFFFFF",
                                            border: "1px solid #E1DAC5",
                                            borderRadius: 4,
                                            fontSize: 12,
                                        }}
                                        formatter={(v, name, item) => [v, item?.payload?.title || name]}
                                    />
                                    <Bar dataKey="passed" fill={forest} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Average quiz score by chapter
                        </div>
                        <h3 className="font-display text-xl text-primary mt-1 mb-4">
                            Mastery signal
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.chapter_funnel}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E1DAC5" />
                                    <XAxis
                                        dataKey="chapter"
                                        stroke="#5C6B64"
                                        fontSize={12}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#5C6B64"
                                        fontSize={11}
                                        tickLine={false}
                                        domain={[0, 10]}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: "#FFFFFF",
                                            border: "1px solid #E1DAC5",
                                            borderRadius: 4,
                                            fontSize: 12,
                                        }}
                                    />
                                    <Bar dataKey="avg_score" fill={gold} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border" data-testid="integrity-flags-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                    Honour-code monitor
                                </div>
                                <h3 className="font-display text-xl text-primary mt-1">
                                    Flagged quiz attempts
                                </h3>
                            </div>
                            {(() => {
                                const total = (data.chapter_funnel || [])
                                    .reduce((s, c) => s + (c.flagged_attempts || 0), 0);
                                return total > 0 ? (
                                    <Badge
                                        className="bg-amber-500/15 text-amber-700 border border-amber-500/30 hover:bg-amber-500/20 gap-1.5"
                                        data-testid="integrity-total-badge"
                                    >
                                        <ShieldAlert className="h-3.5 w-3.5" />
                                        {total} flagged
                                    </Badge>
                                ) : (
                                    <Badge
                                        className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 hover:bg-emerald-500/20 gap-1.5"
                                        data-testid="integrity-total-badge"
                                    >
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        All clean
                                    </Badge>
                                );
                            })()}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 mb-4">
                            An attempt is auto-flagged when the student switches tabs
                            or loses window focus twice or more during the quiz.
                        </p>
                        {(() => {
                            const flagged = (data.chapter_funnel || []).filter(
                                (c) => (c.flagged_attempts || 0) > 0,
                            );
                            if (flagged.length === 0) {
                                return (
                                    <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border rounded-md">
                                        No suspicious activity detected on any chapter
                                        quiz.
                                    </div>
                                );
                            }
                            return (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Chapter</TableHead>
                                            <TableHead className="text-right">
                                                Flagged
                                            </TableHead>
                                            <TableHead className="text-right">
                                                Attempts
                                            </TableHead>
                                            <TableHead className="text-right">
                                                Rate
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {flagged.map((c, i) => {
                                            const rate = c.attempts
                                                ? Math.round(
                                                      (c.flagged_attempts /
                                                          c.attempts) *
                                                          100,
                                                  )
                                                : 0;
                                            return (
                                                <TableRow
                                                    key={`${c.chapter}-${i}`}
                                                    data-testid={`integrity-row-${i}`}
                                                >
                                                    <TableCell>
                                                        <div className="font-medium">
                                                            {c.chapter}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground truncate max-w-[16rem]">
                                                            {c.title}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Badge className="bg-amber-500/15 text-amber-700 border border-amber-500/30 hover:bg-amber-500/20">
                                                            {c.flagged_attempts}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right text-muted-foreground">
                                                        {c.attempts}
                                                    </TableCell>
                                                    <TableCell className="text-right text-muted-foreground">
                                                        {rate}%
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            );
                        })()}
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                            Leaderboard
                        </div>
                        <h3 className="font-display text-xl text-primary mt-1 mb-4">
                            Top students by progress
                        </h3>
                        {data.top_students.length === 0 ? (
                            <div className="text-sm text-muted-foreground italic py-8 text-center">
                                No enrollments yet.
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Progress</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.top_students.map((s) => (
                                        <TableRow key={s.user_id}>
                                            <TableCell>
                                                <div className="font-medium">{s.name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {s.email}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Progress
                                                        value={s.progress_percent}
                                                        className="h-2 w-24"
                                                    />
                                                    <span className="text-xs text-muted-foreground">
                                                        {s.progress_percent}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {s.completed ? (
                                                    <Badge className="bg-accent text-accent-foreground">
                                                        Completed
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline">In progress</Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
